import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/contexts/UserContext';
import { billingService } from '@/services/billingService';

export interface Subscription {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  trial_days: number;
  plan_name: 'solo' | 'team' | 'organization';
  created_at: string;
  updated_at: string;
}

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const fetchSubscription = useCallback(async () => {
    // Don't fetch if user is not logged in or no token
    const accessToken = localStorage.getItem('access_token');
    if (!user?.id || !accessToken) {
      setSubscription(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      const data = await billingService.getSubscription();
      setSubscription(data || null);
      setError(null);
    } catch (err: any) {
      // Handle different error scenarios
      const status = err?.response?.status;
      const message = err?.message || '';
      
      // Silent errors (expected scenarios)
      if (
        status === 401 || // Token expired (will be handled by apiService)
        status === 404 || // No subscription found
        message.includes('No access token')
      ) {
        setError(null);
      } else {
        // Unexpected errors - log them
        console.error('Error fetching subscription:', err);
        setError(err.message || 'Failed to fetch subscription');
      }
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const hasActiveSubscription = !!subscription && subscription.status === 'active';
  const isOnTrial = !!subscription && subscription.status === 'trialing';
  const planName = subscription?.plan_name || null;
  
  // Determine if user has premium features based on subscription
  const hasPremiumFeatures = hasActiveSubscription || isOnTrial;
  
  // Determine if user should see team features
  const hasTeamFeatures = hasPremiumFeatures && (planName === 'team' || planName === 'organization');
  
  // Determine if user should see organization features
  const hasOrganizationFeatures = hasPremiumFeatures && planName === 'organization';

  return {
    subscription,
    loading,
    error,
    hasActiveSubscription,
    isOnTrial,
    planName,
    hasPremiumFeatures,
    hasTeamFeatures,
    hasOrganizationFeatures,
    refetch: fetchSubscription,
  };
};
